import { Dropdown, GoogleMapModal, TextInput } from '@components/common';
import { useStepperStore } from '@store/stepperStore';
import { getIn, useFormik } from 'formik';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { defaultAddMicroSiteLeadFormInitialValues, MicroSite } from '.';
import { Modal } from '@components/common/modal/modal';
import { useGoogleMapStore } from '@store/googleMapStore';
import { useValidation } from '@hooks/validations';
import { useFetchGrades } from '@services/hooks/competition-conversion/useFetchGrades';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { useCompetitionConversionUsageQuery } from '@services/hooks/competition-conversion/useCompetitionConversionUsageQuery';
import { useSiteLeadStore } from '@store/siteLeadStore';
import { useSetMainHeader } from '@hooks/useSetMainHeader';
import { useSiteLeadQuery } from '@services/hooks/site-leads';
import { AllEndUserObject } from '@components/members/components/allEndUserList';
import { DropdownOptions } from '@components/common/formControl/dropdown';
import { ISpecialistDetails } from '@services/hooks/site-specialist';
import { useAddMicroSiteMutation } from '@services/hooks/site-leads/useAddMicroSiteMutation';
import { useGetRxDb } from '@hooks/getRxdbData';
import { ToastType, useTosterStore } from '@store/toster';
import { useLocation, useNavigate } from 'react-router-dom';
import { MicroSiteForm } from './micrositeCard';
import { keyDownEvent } from '@helper/utils';
import { useSiteSummaryRxDb } from '@services/hooks/site-summary-listing/useSiteSummaryRxDb';
import { getDatabase } from '@rxdb/config/databaseConfig';

export const AddNewMicroSite = (): ReactElement => {
	const { state } = useLocation();
	const navigate = useNavigate();

	const siteInfo = state?.siteInfo;
	const isEditMode = Boolean(siteInfo?.id);

	useSetMainHeader(isEditMode ? 'Site Details' : 'Add New Site Lead');

	const [addFields, setAddFields] = useState({});
	const [microSiteList, setMicroSiteList] = useState<MicroSite[] | []>([]);

	const { setCurrentStep } = useStepperStore();
	const { currentAddress } = useGoogleMapStore();
	const { AddMicroSiteLeadValidationSchema } = useValidation();
	const { mutate: postMicroSiteAddData } = useAddMicroSiteMutation();

	const [locationModal, setLocationModal] = useState<boolean>(false);
	const { error: gradeListError, isPending: isGradeListPending } = useFetchGrades();
	const { sfid, addNewSiteLead, setKeyBaseValue, siteDetails, siteVisitId } = useSiteLeadStore();
	const { result: allEndUser } = useGetRxDb<AllEndUserObject>('all');
	const [siteSpeciaListtList, setSiteSpeciaListtList] = useState<ISpecialistDetails[]>([]);

	const { error: competitionListError, isPending: iscompetitionListPending } = useCompetitionConversionUsageQuery();
	const { data: siteData, isFetching: isSiteDataFetching, error: siteDataError } = useSiteLeadQuery();
	const { result: siteDeatilsInfo, isFetching } = useSiteSummaryRxDb({
		sitenumber: siteInfo?.sitenumber_c,
	});
	const { setToaster } = useTosterStore();
	const newFormRef = useRef<HTMLDivElement | null>(null);

	const formik = useFormik({
		initialValues: defaultAddMicroSiteLeadFormInitialValues,
		onSubmit: () => {},
		validationSchema: AddMicroSiteLeadValidationSchema,
	});

	const handleNext = useCallback(
		(isExit: boolean): void => {
			const { siteArea, siteLocation, siteSpecialist, siteName, superVisorName, superVisorNumber } = formik.values;
			const user = allEndUser.find((user) => user.membershipno === siteDetails?.membershipno);
			const keyBaseValues = {
				site_area: siteArea,
				site_location: siteLocation,
				name: siteName,
				supervisor_name: superVisorName,
				supervisor_phone_number: superVisorNumber,
				contract_phone: siteDetails?.contractor_name,
				contract_name: siteDetails?.number,
				end_user_c: user?.sfid,
				ss_sfid: siteSpecialist ? addFields?.ss_sfid : '',
				cmdi_code_c: siteSpecialist,
			};

			Object.entries(keyBaseValues).forEach(([key, value]) => {
				setKeyBaseValue(key, value);
			});

			const payload = keyBaseValues;
			postMicroSiteAddData(payload, {
				onSuccess: (data) => {
					if (data && Object.keys(data).length) {
						setToaster(true, ToastType.success, 'Added micro site');

						if (isExit) {
							handleSkip();
							return;
						}
						handleAdd();
					}
				},
				onError: (error) => {
					setToaster(true, ToastType.error, error?.message);
				},
			});
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[sfid, formik.values, siteData, setCurrentStep, addNewSiteLead, setKeyBaseValue, siteVisitId, addFields]
	);

	const handleLocationSubmit = useCallback((): void => {
		formik.setFieldValue('siteLocation', currentAddress);
		setLocationModal(false);
	}, [currentAddress, formik]);

	const handleOpenSiteLocationModal = (): void => {
		setLocationModal((prevState) => !prevState);
	};

	const handleCloseSiteLocationModal = (): void => {
		setLocationModal(false);
	};

	useEffect(() => {
		if (siteDetails) {
			formik.setFieldValue('contractorName', siteDetails?.name);
			formik.setFieldValue('contractorNumber', siteDetails?.number);
		}
		getDataFromIndexDb();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteDetails]);

	useEffect(() => {
		if (isEditMode && !isFetching && siteDeatilsInfo && siteDeatilsInfo.length !== 0) {
			formik.setFieldValue('contractorName', siteDeatilsInfo[0]?.contract_name);
			formik.setFieldValue('contractorNumber', siteDeatilsInfo[0]?.contract_phone);
			formik.setFieldValue('siteName', siteDeatilsInfo[0]?.name);
			formik.setFieldValue('siteLocation', siteDeatilsInfo[0]?.site_location_c);
			formik.setFieldValue('siteArea', siteDeatilsInfo[0]?.site_area);
			formik.setFieldValue('siteSpecialist', siteDeatilsInfo[0]?.visited_by?.split('-')[1]);
			formik.setFieldValue('superVisorName', siteDeatilsInfo[0]?.supervisor_name);
			formik.setFieldValue('superVisorNumber', siteDeatilsInfo[0]?.supervisor_phone_number);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteDeatilsInfo, isEditMode]);

	const handleNextValidate = (isExit: boolean): void => {
		handleNext(isExit);
	};

	const handleAdd = (): void => {
		const cleanedSite: MicroSite = {
			siteName: formik.values.siteName ?? '',
			siteArea: formik.values.siteArea ?? '',
			siteSpecialist: formik.values.siteSpecialist ?? '',
			superVisorName: formik.values.superVisorName ?? '',
			superVisorNumber: formik.values.superVisorNumber ?? '',
			siteLocation: formik.values.siteLocation ?? '',
			contractorName: formik.values.contractorName ?? '',
			contractorNumber: formik.values.contractorNumber ?? '',
			id: `${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
		};

		setMicroSiteList((prev) => [...prev, cleanedSite]);
		formik.resetForm({
			values: {
				...defaultAddMicroSiteLeadFormInitialValues,
				contractorName: formik.values.contractorName,
				contractorNumber: formik.values.contractorNumber,
			},
		});
	};

	const handleSkip = (): void => {
		if (state?.fromContractor) {
			navigate('/');
			return;
		}
		setCurrentStep(4);
	};

	const getFieldError = (field: string): string | null => {
		const isTouched = getIn(formik.touched, field);
		const error = getIn(formik.errors, field);

		if (isTouched && typeof error === 'string') {
			return error;
		}

		return null;
	};

	const isFieldDisabled = (fieldValue: string): boolean => {
		return !!(isEditMode && fieldValue);
	};

	const shouldShowLoader = (...flags: Array<boolean | undefined>): boolean => flags.some(Boolean);
	const getFirstError = (...errors: Array<Error | null | undefined>): Error | null => {
		const found = errors.find((error): error is Error => error != null);
		return found ?? null;
	};
	const getDataFromIndexDb = async (): Promise<void> => {
		try {
			const db = await getDatabase();
			const result = await db.sitespecialist_list.find().exec();
			if (result?.length > 0) {
				const tempData: ISpecialistDetails[] = result.map((ele: { _data: ISpecialistDetails[] }) => ele?._data);

				setSiteSpeciaListtList(tempData);
			}
		} catch (error) {
			setSiteSpeciaListtList([]);
		}
	};

	useEffect(() => {
		if (newFormRef.current) {
			const element = newFormRef.current;
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
			setTimeout(() => {
				window.scrollBy({ top: -80, behavior: 'smooth' });
			}, 300);
		}
	}, [microSiteList]);
	return (
		<section className="new-site-section">
			<Wrapper
				isLoaderVisible={shouldShowLoader(isGradeListPending, iscompetitionListPending, isSiteDataFetching)}
				isError={getFirstError(gradeListError, competitionListError, siteDataError)}
			>
				<div className="container">
					{microSiteList &&
						microSiteList.length > 0 &&
						microSiteList.map((microSite, index) => (
							<MicroSiteForm key={microSite.id} data={microSite} index={index + 1} />
						))}
					<div ref={newFormRef}>
						{!isEditMode && <h3>Site {microSiteList.length + 1}</h3>}

						<form onSubmit={formik.handleSubmit}>
							<ul className="new-site-wrapper row">
								<li className="form-control d-col d-col-2">
									<TextInput
										id="siteName"
										name="siteName"
										type="text"
										onBlur={formik.handleBlur}
										value={formik.values.siteName ?? ''}
										setFieldValue={formik.setFieldValue}
										label="Site Name"
										error={getFieldError('siteName')}
										disabled={isFieldDisabled('siteName')}
										isAutoFocus
										required
									/>
								</li>
								<li className="form-control d-col d-col-2">
									<TextInput
										id="siteArea"
										name="siteArea"
										type="text"
										onBlur={formik.handleBlur}
										value={formik.values.siteArea ?? ''}
										setFieldValue={formik.setFieldValue}
										label="Site Area"
										error={getFieldError('siteArea')}
										disabled={isFieldDisabled('site_area')}
										required
									/>
								</li>

								<li className="form-control d-col d-col-2  location">
									<TextInput
										id="siteLocation"
										name="siteLocation"
										onBlur={formik.handleBlur}
										type="text"
										value={formik.values.siteLocation ?? ''}
										setFieldValue={formik.setFieldValue}
										label="Site Address"
										error={getFieldError('siteLocation')}
										disabled={isFieldDisabled('site_location_c')}
									/>
									<button
										type="button"
										aria-label="map link"
										className="icon-location map"
										onClick={handleOpenSiteLocationModal}
										disabled={!!(isEditMode && siteInfo?.site_location_c)}
									/>
								</li>
								{siteSpeciaListtList && (
									<li className="d-col d-col-2 ">
										<Dropdown
											id="siteSpecialist"
											name="siteSpecialist"
											label="Site Specialist Name"
											onBlur={(e) => {
												if (!e.target.value) {
													formik.setFieldValue('siteSpecialist', '');
												}
												formik.handleBlur(e);
											}}
											title={formik.values.siteSpecialist ?? ''}
											options={siteSpeciaListtList.map(
												(user): DropdownOptions => ({
													id: user.sfid,
													name: user.sfid,
													value: user.mh2code_c,
												})
											)}
											setFieldValue={formik.setFieldValue}
											onChange={(e) => {
												const user = siteSpeciaListtList.find((user) => user.sfid === e.id);
												setAddFields({
													...addFields,
													name: user?.mh2code_c,
													ss_sfid: user?.sfid,
												});
											}}
											error={getFieldError('siteSpecialist')}
											disabled={isFieldDisabled('site_specialist_code')}
											required
										/>
									</li>
								)}
								<li className="d-col d-col-2 ">
									<Dropdown
										id="contractorName"
										name="contractorName"
										label="Contractor Name"
										onBlur={(e) => {
											if (!e.target.value) {
												formik.setFieldValue('contractorNumber', '');
											}
											formik.handleBlur(e);
										}}
										title={formik.values.contractorName ?? ''}
										options={allEndUser.map(
											(user): DropdownOptions => ({
												id: user.sfid,
												name: user.sfid,
												value: user.name,
											})
										)}
										setFieldValue={formik.setFieldValue}
										disabled
										onChange={(e) => {
											const user = allEndUser.find((user) => user.sfid === e.id);
											formik.setFieldValue('contractorNumber', user?.phone);
											setAddFields({
												...addFields,
												name: user?.name,
												number: user?.phone,
												sfid: user?.sfid,
												membershipno: user?.membershipno,
											});
										}}
										error={getFieldError('contractorName')}
										required
									/>
								</li>

								<li className="d-col d-col-2 ">
									<Dropdown
										id="contractorNumber"
										name="contractorNumber"
										label="Contractor Number"
										onBlur={formik.handleBlur}
										title={formik.values.contractorNumber ?? ''}
										error={getFieldError('contractorNumber')}
										options={allEndUser.map(
											(user): DropdownOptions => ({
												id: user.sfid,
												name: user.sfid,
												value: user.phone,
											})
										)}
										setFieldValue={formik.setFieldValue}
										disabled
										required
									/>
								</li>

								<li className="form-control d-col d-col-2">
									<TextInput
										id="superVisorName"
										name="superVisorName"
										type="text"
										onBlur={formik.handleBlur}
										value={formik.values.superVisorName ?? ''}
										setFieldValue={formik.setFieldValue}
										label="Site In-Charge Name"
										error={getFieldError('superVisorName')}
										disabled={isFieldDisabled('supervisor_name')}
									/>
								</li>

								<li className="form-control d-col d-col-2">
									<TextInput
										id="superVisorNumber"
										name="superVisorNumber"
										type="text"
										onBlur={formik.handleBlur}
										value={formik.values.superVisorNumber ?? ''}
										setFieldValue={formik.setFieldValue}
										label="Site In-Charge Number"
										error={getFieldError('superVisorNumber')}
										disabled={isFieldDisabled('supervisor_phone_number')}
										onKeyDown={keyDownEvent}
										maxLength={10}
									/>
								</li>
							</ul>
						</form>
					</div>
					<Modal
						modalId="googleMapModal"
						className="map-popup dialog-sm md-large pt-4"
						modalOpen={locationModal}
						onModalClose={handleCloseSiteLocationModal}
					>
						<GoogleMapModal handleConfirm={handleLocationSubmit} handleClose={handleCloseSiteLocationModal} />
					</Modal>
					{!isEditMode && (
						<div className="progress-bar-wrapper">
							<div className="progress-bar-main-wrapper">
								{' '}
								<div className="button-row"></div>
								<div className="button-row">
									<button type="button" className="btn btn-primary btn-small" onClick={handleSkip}>
										Skip
									</button>
									<button
										type="submit"
										className="btn btn-primary btn-small"
										onClick={() => handleNextValidate(true)}
										disabled={!formik.isValid || !formik.dirty}
									>
										Save & Exit
									</button>
									<button
										type="submit"
										className="btn btn-primary btn-small"
										onClick={() => handleNextValidate(false)}
										disabled={!formik.isValid || !formik.dirty}
									>
										Save & Add Another
									</button>
								</div>
							</div>
						</div>
					)}
				</div>
			</Wrapper>
		</section>
	);
};
